import {computed, ref} from "vue";
import {debounce} from "lodash";

export const useVideoRecorder = () => {
  let recordingTimeout;
  let mediaRecorder;
  let recordedBlobs;

  const mimeTypes = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/mp4",
  ];

  let props;
  let emits;

  const supports = navigator.mediaDevices.getSupportedConstraints();

  const elapsedSeconds = ref(0);
  const maxSeconds = ref(60);
  const recordButton = ref(null);
  const playButton = ref(null);
  const hasRecording = ref(false);
  const isRecording = ref(false);
  const isPlaying = ref(false);
  const hasPermissions = ref(false);
  const autoplayEnabled = ref(true);
  const facingMode = ref("user");
  const fileInput = ref(null);

  const gumVideo = ref(null);
  const recordedVideo = ref(null);

  const countdownTimeout = () => setTimeout(() => {
    if (!isRecording.value) return;

    if (elapsedSeconds.value < maxSeconds.value) {
      elapsedSeconds.value += 1;
      countdownTimeout();
      return;
    }

    stopRecording();
  }, 1000);

  const countdown = () => {
    if (elapsedSeconds.value >= maxSeconds.value) elapsedSeconds.value = 0;
    countdownTimeout();
  };

  const handleDataAvailable = (event) => {
    if (event.data && event.data.size > 0) {
      recordedBlobs.push(event.data);
    }
  };

  const openFileInput = () => fileInput.value.click();

  const setFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.includes("video")) {
      toast.error("Please select a video file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target.result;
      recordedBlobs = [new Blob([arrayBuffer], {type: file.type})];
      hasRecording.value = true;
      setPlayContent(file.type);
    };

    reader.readAsArrayBuffer(file);
  };

  const getMimeType = () => {
    for (let i = 0; i < mimeTypes.length; i++) {
      if (MediaRecorder.isTypeSupported(mimeTypes[i])) {
        return mimeTypes[i];
      }
    }

    toast.error("Uh oh, your browser doesn't support saving recorded videos. Please try again with a different browser, such as Safari or Chrome.");
    return null;
  };

  const toMMSS = (secondsInt) => {
    let hours = Math.floor(secondsInt / 3600);
    let minutes = Math.floor((secondsInt - hours * 3600) / 60);
    let seconds = secondsInt - hours * 3600 - minutes * 60;

    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return minutes + ":" + seconds;
  };

  const timeRemainingReadable = computed(() => {
    return toMMSS(elapsedSeconds.value) + " / " + toMMSS(maxSeconds.value);
  });

  const startRecording = () => {
    countdown();
    recordedBlobs = [];
    const mimeType = getMimeType();
    const options = {mimeType};

    try {
      mediaRecorder = new MediaRecorder(window.stream, options);
      hasPermissions.value = true;
    } catch (e) {
      console.error("Exception while creating MediaRecorder:", e);
      toast.error("Your browser doesn't support recording videos. Please try again with a different browser, such as Safari or Chrome.");
      console.error("Exception while creating MediaRecorder:", e);
      hasPermissions.value = false;
      return;
    }

    isRecording.value = true;
    playButton.value.disabled = true;

    mediaRecorder.ondataavailable = handleDataAvailable;
    mediaRecorder.start(1000);
  };

  const stopRecording = () => {
    mediaRecorder.stop();
    elapsedSeconds.value = 0;
    isRecording.value = false;
    playButton.value.disabled = false;
    hasRecording.value = true;
    setTimeout(() => {
      setPlayContent(mediaRecorder.mimeType);
    }, 100);
  };

  const handleSuccess = (stream) => {
    hasPermissions.value = true;
    if (recordButton.value) recordButton.value.disabled = false;
    window.stream = stream;

    if (!gumVideo.value) return;
    gumVideo.value.srcObject = stream;
  };

  const initStream = async (constraints) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      handleSuccess(stream);
    } catch (e) {
      console.error("navigator.getUserMedia error:", e);
      hasPermissions.value = false;
    }
  };

  const vw = () => window.innerWidth || document.documentElement.clientWidth || 0;
  const vh = () => window.innerHeight || document.documentElement.clientHeight || 0;

  let hasStartedStream = false;
  const startStream = async () => {
    hasStartedStream = true;
    const constraints = {
      audio: {
        echoCancellation: {ideal: true},
      },
      video: {
        width: vw(),
        height: vh(),
        facingMode: {ideal: facingMode.value},
      },
    };
    await initStream(constraints);
  };

  const toggleFacingModeSupported = !!supports["facingMode"];
  const toggleFacingMode = async () => {
    if (!toggleFacingModeSupported) {
      toast.warning("You don't have a backwards facing camera on this device.");
      return;
    }

    if (facingMode.value === "user") facingMode.value = "environment";
    else facingMode.value = "user";

    stopAllTracks();
    await startStream();
  };

  const toggleIsRecording = () => {
    clearTimeout(recordingTimeout);
    if (!isRecording.value) startRecording();
    else stopRecording();
  };

  const blobToFile = (theBlob, fileName) => {
    return new File([theBlob], fileName, {
      type: theBlob.type,
      lastModified: Date.now(),
    });
  };

  const setPlayContent = async (mimeType) => {
    recordedVideo.value.volume = 0.5;
    recordedVideo.value.src = null;
    recordedVideo.value.srcObject = null;
    const superBuffer = new Blob(recordedBlobs, {type: mimeType});
    recordedVideo.value.src = window.URL.createObjectURL(superBuffer);
    emits("update:file", blobToFile(superBuffer, "video" + mimeType.replace("video/", ".")));
    emits("update:path", recordedVideo.value.src);
    await togglePlay();
  };

  const togglePlay = async () => {
    if (isPlaying.value) {
      isPlaying.value = false;
      recordedVideo.value.pause();
    } else {
      await attemptAutoplay();
    }
  };

  const attemptAutoplay = async () => {
    try {
      await recordedVideo.value.play();
      autoplayEnabled.value = true;
      isPlaying.value = true;
    } catch (e) {
      console.error("Autoplay with audio is disabled:", e);
      autoplayEnabled.value = false;
      isPlaying.value = false;
      console.info("Autoplay with audio is disabled");
    }
  };

  const reRecord = async () => {
    isPlaying.value = false;
    if (typeof recordedVideo.value.stop === "function") {
      recordedVideo.value.stop();
    }
    hasRecording.value = false;
    if (!hasStartedStream) {
      await startStream();
    }
  };

  const getFile = async () => {
    return new Blob(recordedBlobs, {type: getMimeType()});
  };

  const save = async (close = false) => {
    if (recordedBlobs && recordedBlobs.length) emits("save", getFile());
    if (close) closeRecorder();
  };

  const closeRecorder = () => emits("close");

  const init = async (containerProps, containerEmits) => {
    props = containerProps;
    emits = containerEmits;

    hasRecording.value = false;
    maxSeconds.value = props.maxSeconds;

    recordButton.value.addEventListener("click", toggleIsRecording);
    await startStream();
  };

  const stopAllTracks = () => {
    if (window.stream) window.stream.getTracks().forEach((track) => track.stop());
    hasStartedStream = false;
  };

  const debouncedStartStream = debounce(async () => {
    if (isRecording.value) return;
    stopAllTracks();
    await startStream();
  }, 300);

  const handlePlay = () => isPlaying.value = true;
  const handlePause = () => isPlaying.value = false;

  return {
    elapsedSeconds,
    recordButton,
    playButton,
    hasRecording,
    isRecording,
    isPlaying,
    gumVideo,
    recordedVideo,
    hasPermissions,
    autoplayEnabled,
    timeRemainingReadable,
    props,
    fileInput,
    toggleFacingModeSupported,
    debouncedStartStream,
    handlePlay,
    handlePause,
    toggleIsRecording,
    togglePlay,
    reRecord,
    startStream,
    getFile,
    init,
    save,
    closeRecorder,
    toggleFacingMode,
    openFileInput,
    setFile,
    stopAllTracks,
  };
};
